/**
 * Flight Line drafts — the review queue for agent-written pieces.
 * Left: the drafts. Right: one draft to read, edit, approve or reject.
 * Approving publishes to WordPress and syncs it onto next.* at once.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { api, type MeResponse } from '../api/client'
import type { DraftStatus, SectionDraftDetail, SectionDraftSummary, SectionQueueCadence, SectionQueueView, SectionTopic } from '../api/client'
import { fmtRelative } from '../lib/format'
import type { SectionLink } from '../lib/sections'
import { customTopic, isSwitchedOn, pieceTypeKeys, pieceTypeLabel, retryTopic } from '../lib/sections'
import type { TopicRequest } from '../lib/sections'
import { SocialBlock } from './SocialBlock'
import { DraftBriefForm } from './DraftBriefForm'
import { canRevise, describeBrief, canPublishPublicCopy, DESTINATION_OPTIONS, publishTarget, reviseBlockedHint, formBrief, supportsMembership, unpublishTarget, withFormat, type Brief, type Destination } from '../lib/brief'
import { describeError } from '../lib/describeError'
import { approvePiece } from '../editorial/api'
import { ReviewSources } from './ReviewSources'
import { ReviseForm } from './ReviseForm'
import { sourceHref } from '../lib/reviewSources'
import { usePublicBaseUrls } from '../lib/publicBaseUrls'
import { escapeHtml, safeHref } from '../lib/html'
import { choosePhoto as choosePhotoApi, getPhotoOptions, pushPhotoCredit, uploadPhoto as uploadPhotoApi } from '../editorial/piece/photoApi'
import { topicFromQuery } from '../editorial/desk'
import { settingsHref } from '../editorial/links'
import { slotsText } from '../editorial/schedule'
import { photoLabel, photoOk, usedOnLabel } from '../editorial/piece/photoFit'

const SectionCtx = createContext<SectionLink>({ slug: 'flight_journal', label: 'Flight Line', path: 'flight-line' })
const usePub = () => useContext(SectionCtx).slug

const STATUS_LABEL: Record<string, string> = {
  draft: 'Ready to review', needs_attention: 'Needs attention', queued: 'Queued', approved: 'Approved', published: 'Published', unpublished: 'Unpublished', rejected: 'Rejected', failed: 'Failed', sources_ready: 'Sources ready', superseded: 'Earlier version',
}
const STATUS_CLASS: Record<string, string> = {
  draft: 'bg-emerald-900/40 text-emerald-200', needs_attention: 'bg-amber-900/40 text-amber-200', queued: 'bg-sky-900/40 text-sky-200', approved: 'bg-sky-900/40 text-sky-200',
  published: 'bg-ink-700 text-paper', unpublished: 'bg-amber-900/40 text-amber-200', rejected: 'bg-ink-800 text-ink-300', failed: 'bg-red-900/40 text-red-200', sources_ready: 'bg-sky-900/40 text-sky-200', superseded: 'bg-ink-800 text-ink-300',
}

// Where the photo caption's credit came from. 'uncredited' is a marker
// only: a draft with no credit still creates, reviews and publishes.
const CAPTION_NOTE: Record<string, string> = {
  editor: ' · edited here',
  wp: ' · credited in WordPress',
  expanded: " · \u201Cauthor\u2019s collection\u201D resolved from the article byline",
  inherited: ' · credit taken from the article byline',
  uncredited: '',
  none: '',
}

export function SectionDraftsPage({ section }: { section: SectionLink }) {
  const PUB = section.slug
  const qc = useQueryClient()
  const [selected, setSelected] = useState<number | null>(null)
  const [search] = useSearchParams()
  // "Open draft" from the drawer or the status line: select it AND bring
  // the draft panel into view, flashing it. Selecting alone is invisible
  // when the newest draft is already selected and the panel is below the fold.
  const viewRef = useRef<HTMLDivElement>(null)
  const [flash, setFlash] = useState(false)
  const openDraft = (id: number) => {
    setSelected(id)
    requestAnimationFrame(() => viewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    setFlash(true)
    setTimeout(() => setFlash(false), 1800)
  }
  // The ranked subjects are the way to start a draft, so they open by default.
  const [showTopics, setShowTopics] = useState(true)
  const list = useQuery({
    queryKey: ['section-drafts', PUB],
    queryFn: () => api.get<{ items: SectionDraftSummary[]; total: number }>(`/api/lab/section-drafts?publication_slug=${PUB}`),
    refetchInterval: 15_000,
  })
  // What the drafting thread is doing. Polled every 3 s while a draft
  // runs (they take three to five minutes), every 15 s otherwise.
  const [running, setRunning] = useState(false)
  const status = useQuery({
    queryKey: ['section-draft-status', PUB],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${PUB}`),
    refetchInterval: running ? 3_000 : 15_000,
  })
  const lastSeen = useRef<string | null>(null)
  useEffect(() => {
    const st = status.data
    if (!st) return
    setRunning(!!st.running)
    // A run just finished: show the new draft rather than letting it slip into the list.
    const fin = st.last?.finished_at ?? null
    if (fin && fin !== lastSeen.current) {
      if (lastSeen.current !== null || running) {
        qc.invalidateQueries({ queryKey: ['section-drafts', PUB] })
        // Write it finishes on the same row, and a revise supersedes the open
        // row: refresh every open detail, not just the run's own draft.
        qc.invalidateQueries({ queryKey: ['section-draft'] })
        if (st.last?.ok && st.last.draft_id) openDraft(st.last.draft_id)
      }
      lastSeen.current = fin
    }
  }, [status.data, qc, running])
  const draftNow = useMutation({
    mutationFn: ({ topic, brief }: { topic?: TopicRequest; brief: Brief }) => api.post<{ started: boolean; already_running: boolean }>(`/api/lab/section-drafts/draft-now?publication_slug=${PUB}`, { ...(topic ? { topic } : {}), brief }),
    onSuccess: () => { setPending(null); setRunning(true); qc.invalidateQueries({ queryKey: ['section-draft-status', PUB] }) },
  })
  // Every draft start opens the brief form first, pre-filled from the
  // caller's saved defaults, or from the old draft's brief for a redraft.
  const defaults = useQuery({ queryKey: ['section-my-defaults'], queryFn: () => api.get<Brief>(`/api/lab/section-drafts/my-defaults?publication_slug=${PUB}`) })
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const [pending, setPending] = useState<{ topic?: TopicRequest; brief: Brief } | null>(null)
  const [scheduleEdit, setScheduleEdit] = useState(false)
  const fallbackBrief: Brief = { format: 'short', sources: 'both', guidance: '', destination: 'section', include: [], exclude: [], review_sources: false }
  const startDraft = (topic?: TopicRequest, from?: Brief) => {
    setScheduleEdit(false)
    draftNow.reset()
    // Saved defaults carry no review choice, so it follows the format (on for Deep);
    // a redraft keeps the old brief's choice.
    const base = defaults.data ? formBrief(defaults.data) : fallbackBrief
    setPending({ topic, brief: from ? formBrief(from) : withFormat(base, base.format, false) })
  }
  const draftAgain = (t: TopicRequest) => {
    const old = (t as Partial<SectionTopic>).draft
    const prior = old ? list.data?.items.find((x) => x.id === old.id)?.brief : undefined
    startDraft(t, prior)
  }
  const schedule = useQuery({
    queryKey: ['section-schedule-brief', PUB],
    queryFn: () => api.get<{ brief: Brief; updated_by: string | null; updated_at: string | null }>(`/api/lab/section-drafts/schedule-brief?publication_slug=${PUB}`),
    enabled: me?.role === 'admin',
  })
  const saveSchedule = useMutation({
    mutationFn: (brief: Brief) => api.put(`/api/lab/section-drafts/schedule-brief?publication_slug=${PUB}`, brief),
    onSuccess: () => { setScheduleEdit(false); qc.invalidateQueries({ queryKey: ['section-schedule-brief', PUB] }) },
  })
  useEffect(() => {
    if (selected != null || !list.data?.items.length) return
    const want = Number(search.get('draft'))
    const hit = want ? list.data.items.find((x) => x.id === want) : undefined
    setSelected((hit ?? list.data.items[0]).id)
  }, [list.data, selected, search])

  return (
    <SectionCtx.Provider value={section}>
      <div className="p-8 space-y-6 max-w-[1500px]">
        <header className="flex items-end justify-between border-b border-ink-500 pb-6">
          <div>
            <div className="label mb-1">Editorial Desk</div>
            <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">{section.label} drafts</h1>
            <p className="label mt-2 max-w-2xl normal-case tracking-normal">
              Pieces the agent wrote from what people search for and what the archive holds. Nothing publishes
              until you approve it here. Approving files it in WordPress as a private post (the record, not on the title's www site) and puts it on its next.* site.
            </p>
          </div>
          <div className="flex gap-2">
            {/* A title outside SECTION_AGENT_PUBS drafts nothing: no Topics drawer (its Draft buttons). */}
            {isSwitchedOn(section) && <Button variant="outline" onClick={() => setShowTopics((v) => !v)}>{showTopics ? 'Hide topics' : 'Topics'}</Button>}
          </div>
        </header>
        <DraftProgress status={status.data} onOpen={openDraft} />
        {me?.role === 'admin' && schedule.data && !scheduleEdit && (
          <p className="label normal-case tracking-normal">
            Scheduled drafts use: {describeBrief(schedule.data.brief, section.label)}{' '}
            <button type="button" className="underline" onClick={() => { setPending(null); setScheduleEdit(true) }}>Change</button>
          </p>
        )}
        {scheduleEdit && schedule.data && (
          <DraftBriefForm initial={schedule.data.brief} title="scheduled drafts" saveDefaults={false} submitLabel="Save"
                          scheduleMode publicationSlug={PUB} sectionLabel={section.label} busy={saveSchedule.isPending} onSubmit={(b) => saveSchedule.mutate(b)} onCancel={() => setScheduleEdit(false)} />
        )}
        {pending && (
          <DraftBriefForm key={pending.topic?.label ?? '(auto)'} initial={pending.brief} title={pending.topic?.label ?? 'the best open subject'}
                          busy={running || draftNow.isPending} publicationSlug={PUB} sectionLabel={section.label}
                          onSubmit={(brief) => draftNow.mutate({ topic: pending.topic, brief })}
                          onCancel={() => { setPending(null); draftNow.reset() }} />
        )}
        {pending && draftNow.isError && (
          <p className="text-red-300 text-[13px]">{describeError(draftNow.error)}</p>
        )}
        {showTopics && isSwitchedOn(section) && <TopicsDrawer onDraft={(t) => (t.draft ? draftAgain(t) : startDraft(t))} onOpen={openDraft} running={running} draftingLabel={status.data?.topic_label ?? null} />}
        <div className="grid grid-cols-[380px_minmax(0,1fr)] gap-6">
          <div className="space-y-4">
            <Panel label="Review queue">
              <ul className="divide-y divide-ink-700">
                {(list.data?.items ?? []).map((d) => (
                  <li key={d.id}>
                    <button type="button" onClick={() => setSelected(d.id)}
                            className={`w-full text-left px-3 py-3 hover:bg-ink-800 ${selected === d.id ? 'bg-ink-800' : ''}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[d.status] ?? ''}`}>{STATUS_LABEL[d.status] ?? d.status}</span>
                        {d.grounding === 'web' && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200">web-sourced</span>}
                        <span className="label ml-auto">{fmtRelative(d.created_at)}</span>
                      </div>
                      <div className="text-paper text-[14px] leading-snug">{d.title ?? d.topic.label ?? '(untitled)'}</div>
                      <div className="label mt-1">{d.topic.label} · {d.topic.impressions ?? 0} impressions · {d.word_count} words</div>
                    </button>
                  </li>
                ))}
                {list.data && list.data.items.length === 0 && <li className="px-3 py-6 label">No drafts yet. Pick a subject above and press Draft this.</li>}
              </ul>
            </Panel>
            <QueuePanel onChange={() => qc.invalidateQueries({ queryKey: ['section-drafts', PUB] })} />
          </div>
          <div ref={viewRef} className={`scroll-mt-6 rounded transition-shadow duration-700 ${flash ? 'ring-2 ring-amber-glow shadow-[0_0_0_6px_rgba(251,191,36,0.15)]' : ''}`}>
            {selected != null && <DraftView id={selected} onChange={() => qc.invalidateQueries({ queryKey: ['section-drafts', PUB] })}
                                            onRedraft={(t, b) => startDraft(t, b)} onOpen={openDraft} running={running} />}
          </div>
        </div>
      </div>
    </SectionCtx.Provider>
  )
}

const STAGE_LABEL: Record<string, string> = {
  starting: 'starting', sources: 'gathering sources from the archive', writing: 'writing the piece',
  'fact-check': 'fact-checking every claim against the sources', photo: 'choosing a photo', saving: 'saving',
  social: 'writing the social posts', revising: 'rewriting from the same sources',
}
const mmss = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }

/** The drafting status line: live stage and elapsed time while a draft
 *  runs; the outcome for ten minutes after it finishes. */
function DraftProgress({ status, onOpen }: { status: DraftStatus | undefined; onOpen: (id: number) => void }) {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!status?.running) return
    const t = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [status?.running])
  if (!status) return null
  if (status.running) {
    const elapsed = status.started_at ? mmss(Date.now() - Date.parse(status.started_at)) : ''
    return (
      <div className="flex items-center gap-3 border border-emerald-900/60 bg-emerald-900/20 rounded px-3 py-2">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
        <span className="text-emerald-100 text-[13px]">
          Drafting <strong>{status.topic_label ?? 'the best open subject'}</strong> · {elapsed} · {STAGE_LABEL[status.stage ?? ''] ?? status.stage}
        </span>
        <span className="label ml-auto">three to five minutes; it opens here when done</span>
      </div>
    )
  }
  const last = status.last
  if (!last?.finished_at || Date.now() - Date.parse(last.finished_at) > 10 * 60_000) return null
  return last.ok
    ? <p className="label text-emerald-200">Drafted <strong className="text-paper">{last.topic_label}</strong> {fmtRelative(last.finished_at)}.
        {last.draft_id && <button type="button" className="underline ml-2" onClick={() => onOpen(last.draft_id as number)}>Open it</button>}</p>
    : <p className="text-red-300 text-[13px]">Drafting <strong>{last.topic_label ?? 'the best open subject'}</strong> failed {fmtRelative(last.finished_at)}: {last.error}</p>
}

/** Provider name for the chip label. */
const PROVIDER_LABEL: Record<string, string> = {
  wikipedia: 'Wikipedia', reddit: 'Reddit', trends: 'Trends', news: 'News',
  calendar: 'Anniversary', gsc_all: 'Search', web: 'Web',
}

/** A short, provider-specific detail pulled from the signal's evidence
 * string (a ratio for Wikipedia, a rank for Reddit, years for an
 * anniversary, the other title's name for a cross-title search). Falls
 * back to the bare provider label when the evidence doesn't parse — the
 * full evidence is always available on hover via the `title` attribute. */
const DAY = 86400000
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
/** How old the thing itself is: "today", "3d", or the calendar date for a
 *  future anniversary. Null when the source has no date (search aggregates,
 *  the web sweep). Times are UTC days, which is how the providers stamp them. */
function sourceAge(s: { provider: string; source_date: string | null }): string | null {
  if (!s.source_date) return null
  const days = Math.round((Date.now() - Date.parse(s.source_date + 'T00:00:00Z')) / DAY)
  if (s.provider === 'calendar') return `on ${shortDate(s.source_date)}`
  if (days <= 0) return 'today'
  return `${days}d ago`
}
function signalTitle(s: { evidence: string; source_date: string | null; observed_at: string | null }): string {
  const bits = [s.evidence]
  if (s.source_date) bits.push(`source date ${shortDate(s.source_date)}`)
  if (s.observed_at) bits.push(`seen ${shortDate(s.observed_at)}`)
  return bits.join(' — ')
}
/** The most recent source date among a subject's signals, for the freshness line. */
function freshest(signals: { source_date: string | null; provider: string }[]): string | null {
  const dated = signals.filter((s) => s.source_date && s.provider !== 'calendar').map((s) => s.source_date as string).sort()
  return dated.length ? dated[dated.length - 1] : null
}
function signalChipText(s: { provider: string; evidence: string; source_date: string | null }): string {
  const age = sourceAge(s)
  const core = signalChipCore(s)
  return age ? `${core} · ${age}` : core
}
function signalChipCore(s: { provider: string; evidence: string }): string {
  const label = PROVIDER_LABEL[s.provider] ?? s.provider
  if (s.provider === 'wikipedia') {
    const m = s.evidence.match(/^([\d.]+)×/)
    return m ? `${label} ${m[1]}×` : label
  }
  if (s.provider === 'reddit') {
    const m = s.evidence.match(/#(\d+)/)
    return m ? `${label} #${m[1]}` : label
  }
  if (s.provider === 'calendar') {
    const m = s.evidence.match(/^(\d+)\s+years/)
    return m ? `${label} ${m[1]} yrs` : label
  }
  if (s.provider === 'gsc_all') {
    const m = s.evidence.match(/^(.+?)\s+search:/)
    return m ? `${m[1]} search` : label
  }
  return label
}

const CHIP_CLASS = 'text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-ink-700 text-ink-200'

function TopicsDrawer({ onDraft, onOpen, running, draftingLabel }: { onDraft: (t: TopicRequest) => void; onOpen: (id: number) => void; running: boolean; draftingLabel: string | null }) {
  const PUB = usePub()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['section-topics', PUB], queryFn: () => api.get<{ items: SectionTopic[]; computed_at: string | null }>(`/api/lab/section-drafts/topics?publication_slug=${PUB}`) })
  // The refresh gathers signals and reclusters in the background (a
  // couple of minutes); re-read the stored topics a few times while it runs.
  const refreshInterest = useMutation({
    mutationFn: () => api.post<{ started: boolean; already_running: boolean }>(`/api/lab/section-drafts/interest/refresh?publication_slug=${PUB}`),
    onSuccess: () => [45_000, 90_000, 150_000, 240_000].forEach((ms) => setTimeout(() => qc.invalidateQueries({ queryKey: ['section-topics', PUB] }), ms)),
  })
  const computed = q.data?.computed_at ? new Date(q.data.computed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null
  // An editor's own subject, for when the ranked list has nothing they want.
  // The type picker shows only for titles whose subjects carry a type.
  const [search] = useSearchParams()
  const [ownLabel, setOwnLabel] = useState(() => topicFromQuery(search))
  const [ownIntent, setOwnIntent] = useState('')
  const [ownType, setOwnType] = useState('')
  const typed = (q.data?.items ?? []).some((t) => t.piece_type)
  const own = customTopic(ownLabel, ownIntent, typed && ownType ? ownType : null)
  const draftOwn = () => {
    if (!own) return
    onDraft(own)
    setOwnLabel(''); setOwnIntent(''); setOwnType('')
  }
  return (
    <Panel label="What people are searching for and talking about"
           right={<span className="flex items-center gap-2">{computed && <span className="label">computed {computed}</span>}<Button variant="outline" size="sm" disabled={refreshInterest.isPending} onClick={() => refreshInterest.mutate()}>Refresh now</Button></span>}>
      {q.isLoading && <p className="label">Loading topics… (first open after a missed night reclusters, about a minute)</p>}
      {refreshInterest.data?.already_running && <p className="label text-amber-200 mb-2">Already refreshing. Topics update here when it is done (a couple of minutes).</p>}
      {refreshInterest.data?.started && !refreshInterest.data.already_running && <p className="label text-emerald-200 mb-2">Gathering signals and reclustering (a couple of minutes)… topics update here when done.</p>}
      <form className="flex flex-wrap items-end gap-2 mb-4 pb-4 border-b border-ink-700"
            onSubmit={(e) => { e.preventDefault(); draftOwn() }}>
        <label className="flex-1 min-w-[220px]">
          <span className="label">Or draft a subject of your own</span>
          <input value={ownLabel} maxLength={200} onChange={(e) => setOwnLabel(e.target.value)} placeholder="e.g. Grumman F7F Tigercat"
                 className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5" />
        </label>
        <label className="flex-1 min-w-[220px]">
          <span className="label">What should it answer? (optional)</span>
          <input value={ownIntent} maxLength={500} onChange={(e) => setOwnIntent(e.target.value)} placeholder="e.g. why it arrived too late for the war"
                 className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5" />
        </label>
        {typed && (
          <label>
            <span className="label">Type</span>
            <select value={ownType} onChange={(e) => setOwnType(e.target.value)}
                    className="mt-1 block bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5">
              <option value="">Any</option>
              {pieceTypeKeys(PUB).map((k) => <option key={k} value={k}>{pieceTypeLabel(k)}</option>)}
            </select>
          </label>
        )}
        <Button size="sm" variant="outline" type="submit" disabled={running || !own}>
          {running ? 'A draft is being written…' : 'Draft it'}
        </Button>
      </form>
      <table className="w-full text-[13px]">
        <thead><tr className="label text-left"><th className="py-1">Topic</th><th>Searchers want</th><th className="text-right">Impr.</th><th className="text-right">Pos.</th><th className="text-right">Score</th><th /></tr></thead>
        <tbody>
          {(q.data?.items ?? []).map((t) => (
            <tr key={t.label} className="border-t border-ink-700">
              <td className="py-2 text-paper">
                {t.label}
                {pieceTypeLabel(t.piece_type) && (
                  <span className="font-mono text-[10px] tracking-label px-1.5 py-0.5 border border-ink-500 text-paper-dim ml-2">{pieceTypeLabel(t.piece_type)}</span>
                )}
                <div className="label normal-case tracking-normal">{t.representative_query}</div>
                {(t.gsc_score > 0 || t.signals.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.gsc_score > 0 && <span className={CHIP_CLASS}>Search Console {t.impressions}</span>}
                    {t.signals.map((s, i) => {
                      const href = safeHref(s.url)
                      return href
                        ? <a key={i} href={href} target="_blank" rel="noreferrer" title={signalTitle(s)} className={`${CHIP_CLASS} hover:text-amber-glow`}>{signalChipText(s)}</a>
                        : <span key={i} title={signalTitle(s)} className={CHIP_CLASS}>{signalChipText(s)}</span>
                    })}
                  </div>
                )}
              </td>
              <td className="text-ink-200">{t.search_intent}</td>
              <td className="text-right">{t.impressions}</td><td className="text-right">{t.position.toFixed(1)}</td>
              <td className="text-right">
                {t.score.toFixed(0)}
                <div className="label normal-case tracking-normal">{t.gsc_score.toFixed(0)} + {t.interest_bonus.toFixed(0)} × {t.coverage}</div>
                {freshest(t.signals) && <div className="label normal-case tracking-normal">freshest {shortDate(freshest(t.signals) as string)}</div>}
              </td>
              <td className="text-right whitespace-nowrap">
                {running && draftingLabel === t.label
                  ? <Button size="sm" variant="outline" disabled>Drafting…</Button>
                  : t.draft && t.draft.status !== 'rejected' && t.draft.status !== 'failed'
                    ? <span className="inline-flex items-center gap-2">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[t.draft.status] ?? ''}`}>{STATUS_LABEL[t.draft.status] ?? t.draft.status}</span>
                        <Button size="sm" variant="outline" onClick={() => onOpen((t.draft as { id: number }).id)}>Open draft</Button>
                      </span>
                    : <span className="inline-flex items-center gap-2">
                        {t.draft && <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[t.draft.status] ?? ''}`}>{STATUS_LABEL[t.draft.status] ?? t.draft.status}</span>}
                        <Button size="sm" variant="outline" disabled={running} onClick={() => onDraft(t)}>{t.draft ? 'Draft again' : 'Draft this'}</Button>
                      </span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}


// toUTCString() ends in "GMT" ("Wed, 17 Sep 2026 11:00:00 GMT") — same
// instant, wrong word. The UI convention here is to say UTC.
/** "Monday and Wednesday at 7:30 AM ET": the server's Eastern reading of the saved slots. */
const slotText = (c: SectionQueueCadence) => slotsText(c.days_of_week_et, c.release_time_et) ?? 'no days'
const fmtUTC = (iso: string) => new Date(iso).toUTCString().replace(/ GMT$/, ' UTC')

function QueuePanel({ onChange }: { onChange: () => void }) {
  const PUB = usePub()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['section-queue', PUB],
    queryFn: () => api.get<SectionQueueView>(`/api/lab/section-drafts/queue?publication_slug=${PUB}`),
  })
  const reorder = useMutation({
    mutationFn: (draft_ids: number[]) =>
      api.post<SectionQueueView>(`/api/lab/section-drafts/queue/reorder?publication_slug=${PUB}`, { draft_ids }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['section-queue', PUB] }); onChange() },
  })
  // Leaving the queue: returns the piece to `draft`, distinct from Reject
  // (which frees the subject for a new draft) and from letting it publish.
  const unqueue = useMutation({
    mutationFn: (draftId: number) => api.post<SectionDraftDetail>(`/api/lab/section-drafts/${draftId}/unqueue`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['section-queue', PUB] }); onChange() },
  })
  const items = q.data?.items ?? []
  // Up and down rather than drag: the same reorder call, no drag-and-drop
  // library, and it works from a keyboard.
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return
    const ids = items.map((i) => i.id)
    const [moved] = ids.splice(from, 1)
    ids.splice(to, 0, moved)
    reorder.mutate(ids)
  }
  return (
    <Panel label="Publication queue">
      {q.isLoading && <p className="label">Loading queue…</p>}
      {q.isError && <p className="text-red-300 text-[13px] mb-2">{describeError(q.error)}</p>}
      <p className="label mb-2">
        {!q.data?.cadence
          ? 'No release slots saved, so nothing is released. Set them in Settings.'
          : q.data.enabled
            ? `Releases ${slotText(q.data.cadence)} — one piece per slot`
            : `Releases are switched off. Slots are saved (${slotText(q.data.cadence)}) but nothing goes out until releases are turned back on.`}
      </p>
      {q.data?.enabled && q.data?.next_release_at && (
        <p className="label mb-2">Next release: {fmtUTC(q.data.next_release_at)}</p>
      )}
      {!q.isLoading && items.length === 0 ? (
        <p className="label">The queue is empty.</p>
      ) : (
        <ol className="space-y-1">
          {items.map((i, idx) => (
            <li key={i.id} className="flex items-center gap-2 text-[13px]">
              <span className="label w-6">{idx + 1}</span>
              <span className="flex-1 truncate">{i.title ?? `Draft ${i.id}`}</span>
              <span className="label">{i.word_count} words</span>
              {i.featured_caption_source === 'uncredited' && (
                <span className="label text-amber-200">no photo credit</span>
              )}
              <button type="button" className="label underline" disabled={idx === 0 || reorder.isPending}
                      onClick={() => move(idx, idx - 1)}>up</button>
              <button type="button" className="label underline"
                      disabled={idx === items.length - 1 || reorder.isPending}
                      onClick={() => move(idx, idx + 1)}>down</button>
              <button type="button" className="label underline"
                      disabled={unqueue.isPending}
                      onClick={() => unqueue.mutate(i.id)}>unqueue</button>
            </li>
          ))}
        </ol>
      )}
      {reorder.isError && (
        <p className="text-red-300 text-[13px] mt-2">
          Reorder failed: {describeError(reorder.error)} The order below may be stale — reload to check it.
        </p>
      )}
      {unqueue.isError && (
        <p className="text-red-300 text-[13px] mt-2">Unqueue failed: {describeError(unqueue.error)}</p>
      )}
      <CadenceNote />
    </Panel>
  )
}

/** Read-only: slots are saved in Eastern time from Editorial Desk → Settings (the old PUT is 410). */
function CadenceNote() {
  const section = useContext(SectionCtx)
  return (
    <div className="mt-3 border-t border-ink-700 pt-2">
      <p className="label">
        Release slots moved to Editorial Desk → Settings.{' '}
        <Link className="underline" to={settingsHref(section.path)}>Change them in Settings</Link>
      </p>
    </div>
  )
}

function DraftView({ id, onChange, onRedraft, onOpen, running }: { id: number; onChange: () => void; onRedraft: (t: TopicRequest | undefined, brief: Brief) => void; onOpen: (id: number) => void; running: boolean }) {
  const section = useContext(SectionCtx)
  const PUB = section.slug
  const publicBases = usePublicBaseUrls()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['section-draft', id], queryFn: () => api.get<SectionDraftDetail>(`/api/lab/section-drafts/${id}`) })
  // featured_caption_override is write-only: the API takes it on PATCH and
  // answers with the resolved featured_caption, so it is not part of the detail.
  const [edit, setEdit] = useState<Partial<SectionDraftDetail> & { featured_caption_override?: string }>({})
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoCredit, setPhotoCredit] = useState('')
  const [editHtml, setEditHtml] = useState(false)
  const [note, setNote] = useState('')
  const [revising, setRevising] = useState(false)
  useEffect(() => { setEdit({}); setEditHtml(false); setNote(''); setRevising(false) }, [id])
  const refresh = () => { qc.invalidateQueries({ queryKey: ['section-draft', id] }); onChange() }
  const save = useMutation({ mutationFn: () => api.patch<SectionDraftDetail>(`/api/lab/section-drafts/${id}`, edit), onSuccess: () => { setEdit({}); refresh() } })
  // Publishing needs an editor's approval first: a piece still under review is approved through the
  // editorial route (editors and admins only; the server refuses a writer), then published as before.
  const approve = useMutation({
    mutationFn: async () => {
      const status = q.data?.status
      if (status === 'draft' || status === 'needs_attention') await approvePiece(id)
      return api.post<SectionDraftDetail>(`/api/lab/section-drafts/${id}/approve`)
    },
    onSuccess: refresh,
  })
  const addToQueue = useMutation({
    mutationFn: () => api.post<SectionDraftDetail>(`/api/lab/section-drafts/${id}/queue`),
    // Also invalidate the queue panel's own query — it lives beside this
    // view, not under it, so `refresh` alone never touches it.
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ['section-queue', PUB] }) },
  })
  const reject = useMutation({ mutationFn: () => api.post<SectionDraftDetail>(`/api/lab/section-drafts/${id}/reject`, { note }), onSuccess: refresh })
  const setDestination = useMutation({
    mutationFn: (destination: Destination) => api.patch<SectionDraftDetail>(`/api/lab/section-drafts/${id}`, { destination }),
    onSuccess: refresh,
  })
  const publishPublic = useMutation({ mutationFn: () => api.post<SectionDraftDetail>(`/api/lab/section-drafts/${id}/publish-public`), onSuccess: refresh })
  const unpublish = useMutation({ mutationFn: () => api.post<SectionDraftDetail>(`/api/lab/section-drafts/${id}/unpublish`, { note }), onSuccess: refresh })
  // A published piece is otherwise read-only, but its photo credit has to
  // be correctable: the archive sync reads the caption back out of
  // WordPress, so the push is what actually reaches a reader.
  // A photograph the archive cannot supply. The credit is optional; an
  // uncredited one is flagged until an editor types it in.
  // What the picker had to choose from. It refuses anything it cannot tie
  // to the subject, so the reviewer needs to see what was passed over.
  const options = useQuery({
    queryKey: ['section-draft-photo-options', id],
    queryFn: () => getPhotoOptions(id),
    // A draft waiting for source review has no piece, and its photo endpoints answer 409.
    enabled: q.data !== undefined && q.data.status !== 'sources_ready' && q.data.status !== 'superseded',
  })
  const choosePhoto = useMutation({
    mutationFn: (assetId: number) => choosePhotoApi(id, assetId),
    onSuccess: refresh,
  })
  const uploadPhoto = useMutation({
    mutationFn: () => uploadPhotoApi(id, photoFile as File, photoCredit),
    onSuccess: () => { setPhotoFile(null); setPhotoCredit(''); refresh() },
  })
  const pushCredit = useMutation({
    mutationFn: () => pushPhotoCredit(id, edit.featured_caption_override ?? null),
    onSuccess: () => { setEdit((e) => { const { featured_caption_override: _drop, ...rest } = e; return rest }); refresh() },
  })
  if (!q.data) return <Panel label="Draft"><p className="label">Loading…</p></Panel>
  const d = q.data
  const dest: Destination = d.destination ?? d.brief?.destination ?? 'section'
  const destinationPicker = (d.status !== 'published' && d.status !== 'unpublished' && d.status !== 'superseded' && (supportsMembership(PUB) || dest !== 'section')) && (
    <label className="flex items-center gap-1">
      <span className="label">Publish to</span>
      <select value={dest} disabled={setDestination.isPending} onChange={(e) => setDestination.mutate(e.target.value as Destination)}
              className="bg-ink-900 border border-ink-600 text-paper text-[12px] p-1">
        {DESTINATION_OPTIONS(section.label).map((o) => <option key={o.key} value={o.key} title={o.hint}>{o.label}</option>)}
      </select>
    </label>
  )
  const destinationError = setDestination.error ? <p className="text-red-300 text-[13px] mb-2">{describeError(setDestination.error)}</p> : null
  if (d.status === 'sources_ready') {
    // Nothing is written yet: the review panel stands in for the article editor.
    return (
      <Panel label={d.topic.label ?? 'Draft'}>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[d.status] ?? ''}`}>{STATUS_LABEL[d.status] ?? d.status}</span>
          {d.brief && <span className="label">{describeBrief(d.brief, section.label)}</span>}
          {destinationPicker}
          {d.requested_by && <span className="label">asked for by {d.requested_by}</span>}
        </div>
        {destinationError}
        <ReviewSources key={d.id} draft={d} running={running} onChange={refresh} />
        <div className="flex items-center gap-2 mt-4 justify-end">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional)" className="bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5 w-56" />
          <Button variant="danger" disabled={reject.isPending} onClick={() => reject.mutate()}>Reject</Button>
        </div>
        {reject.error && <p className="text-red-300 text-[13px] mt-2">{describeError(reject.error)}</p>}
      </Panel>
    )
  }
  const v = (k: keyof SectionDraftDetail) => (edit[k] ?? d[k]) as string
  // Published and unpublished pieces are read-only here: the WordPress post
  // is the copy of record, and republishing only flips its status back.
  const superseded = d.status === 'superseded'
  const locked = d.status === 'published' || d.status === 'unpublished' || superseded
  const dirty = Object.keys(edit).length > 0
  // An unsupported claim was either cut ("removed") or could not be found
  // in the rendered text and is still in the piece ("not_found"). Labelling
  // the second one "cut" would tell the reviewer the opposite of the truth.
  const removed = d.verification.removed ?? []
  const notFound = d.verification.not_found ?? []
  const fate = (text: string) =>
    removed.includes(text) ? ' — cut'
      : notFound.includes(text) ? ' — NOT located, still in the text'
        : ' — unsupported'
  const field = (label: string, key: keyof SectionDraftDetail, rows = 1) => (
    <label className="block">
      <span className="label">{label}</span>
      <textarea rows={rows} disabled={locked} value={v(key) ?? ''} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })}
                className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
    </label>
  )
  return (
    <div className="space-y-4">
      <Panel label={d.title ?? 'Draft'}>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[d.status] ?? ''}`}>{STATUS_LABEL[d.status] ?? d.status}</span>
          {d.brief && <span className="label">{describeBrief(d.brief, section.label)}</span>}
          {destinationPicker}
          {d.grounding === 'web' && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200">web-sourced: written from open-web sources, check with care</span>}
          <span className="label">{d.word_count} words · {Math.round((d.verification.unsupported_ratio ?? 0) * 100)}% of claims cut · {d.ai_model}{d.requested_by ? ` · asked for by ${d.requested_by}` : ''}</span>
          {safeHref(d.public_url) && <a className="label underline ml-auto" href={safeHref(d.public_url)} target="_blank" rel="noreferrer">{viewLinkLabel(d.public_url as string)}</a>}
        </div>
        {superseded && (
          <div className="mb-3 flex items-center gap-3 bg-amber-900/40 text-amber-200 text-[13px] p-2 rounded">
            <span>This is an earlier version.</span>
            {d.superseded_by_id != null && <Button variant="outline" size="sm" onClick={() => onOpen(d.superseded_by_id as number)}>Newer version</Button>}
          </div>
        )}
        {d.revised_from_id != null && (
          <p className="text-ink-200 text-[13px] mb-3 flex flex-wrap items-center gap-2">
            <span>Revised from #{d.revised_from_id} — “{d.revision_note}”</span>
            <Button variant="outline" size="sm" onClick={() => onOpen(d.revised_from_id as number)}>Earlier version</Button>
          </p>
        )}
        {destinationError}
        {d.error && <p className="text-red-300 text-[13px] mb-3">{d.error}</p>}
        {canPublishPublicCopy({ status: d.status, destination: dest, error: d.error, wp_post_id: d.wp_post_id, member_wp_post_id: d.member_wp_post_id }) && (
          <div className="mb-3">
            <Button variant="outline" disabled={publishPublic.isPending} onClick={() => publishPublic.mutate()}>
              {publishPublic.isPending ? 'Publishing…' : 'Publish the public copy'}
            </Button>
            {publishPublic.error && <p className="text-red-300 text-[13px] mt-1">{describeError(publishPublic.error)}</p>}
          </div>
        )}
        {!!d.verification.attention?.length && <p className="text-amber-200 text-[13px] mb-3">Needs attention: {d.verification.attention.join('; ')}</p>}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {field('Title (on page)', 'title')}{field(dest === 'membership' ? 'SEO title' : 'SEO title (under 60 chars)', 'seo_title')}
          {field('Meta description', 'meta_description', 2)}{field('Slug', 'slug')}
        </div>
        {safeHref(d.featured_image_url) && (
          <figure className="mb-3">
            <img src={safeHref(d.featured_image_url)} alt="" className="max-h-64 rounded border border-ink-600" />
            <label className="block mt-1">
              <span className="label">
                Photo caption{CAPTION_NOTE[d.featured_caption_source ?? 'none'] ?? ''}
              </span>
              <textarea rows={2} disabled={locked && d.status !== 'published'}
                        value={(edit.featured_caption_override ?? d.featured_caption) ?? ''}
                        onChange={(e) => setEdit({ ...edit, featured_caption_override: e.target.value })}
                        className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
            </label>
            {d.featured_caption_source === 'uncredited' && (
              <p className="text-[12px] text-amber-200 mt-1">
                No photo credit found. Check the source article before publishing — do not guess.
              </p>
            )}
            {d.status === 'published' && (
              <p className="mt-1">
                <Button onClick={() => pushCredit.mutate()} disabled={pushCredit.isPending}>
                  {pushCredit.isPending ? 'Sending…' : 'Send credit to the site'}
                </Button>
                <span className="label ml-2">
                  Updates the photo caption on the live piece. It reaches next.* at the next archive sync.
                </span>
              </p>
            )}
            {d.featured_photo_article && (
              <p className="label mt-1">
                Photo from:{' '}
                <Link className="underline" to={`/lab/archive/${d.featured_photo_article.article_id}`}>
                  {d.featured_photo_article.title ?? `article ${d.featured_photo_article.article_id}`}
                </Link>
              </p>
            )}
          </figure>
        )}
        {!superseded && (options.data?.items.length ?? 0) > 0 && (
          <div className="mb-3">
            <span className="label">
              From the archive: {options.data!.items.filter((o) => photoOk(o)).length} of{' '}
              {options.data!.items.length} show the subject
            </span>
            <div className="flex flex-wrap gap-2 mt-1">
              {options.data!.items.map((o) => (
                <button key={o.asset_id} type="button" title={o.article?.title ?? o.maker?.page_title ?? ''}
                        onClick={() => choosePhoto.mutate(o.asset_id)}
                        disabled={choosePhoto.isPending || o.image_url === d.featured_image_url}
                        className={`w-40 text-left border rounded p-1 ${o.image_url === d.featured_image_url
                          ? 'border-amber-400' : photoOk(o) ? 'border-ink-600' : 'border-ink-700 opacity-70'}`}>
                  <img src={o.image_url} alt="" loading="lazy" className="w-full h-20 object-cover rounded" />
                  <span className="label block mt-1 truncate">{o.credit ?? 'no credit'}</span>
                  <span className="label block truncate">
                    {o.image_url === d.featured_image_url ? 'in use' : photoLabel(o)}
                  </span>
                  {o.fit_reason && <span className="label block">{o.fit_reason}</span>}
                  {usedOnLabel(o) && <span className="label block text-amber-400">{usedOnLabel(o)}</span>}
                </button>
              ))}
            </div>
            <p className="label mt-1">
              "Checked" means a model looked at the photograph itself; the others were matched by caption only.
              Photographs that don't fit are shown so you can see what was passed over. Choosing one re-derives its credit.
            </p>
          </div>
        )}
        {!superseded && <div className="mb-3 border border-ink-600 rounded p-2">
          <span className="label">
            {d.featured_image_url ? 'Replace the photograph' : 'No photograph — upload one'}
          </span>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <input type="file" accept="image/jpeg,image/png"
                   onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                   className="text-[13px] text-paper" />
            <input type="text" value={photoCredit} placeholder="Credit (optional), e.g. (Photo: Alex Mladenov)"
                   onChange={(e) => setPhotoCredit(e.target.value)}
                   className="flex-1 min-w-[16rem] bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
            <Button onClick={() => uploadPhoto.mutate()}
                    disabled={!photoFile || uploadPhoto.isPending}>
              {uploadPhoto.isPending ? 'Uploading…' : 'Upload photograph'}
            </Button>
          </div>
          {uploadPhoto.isError && (
            <p className="text-red-300 text-[12px] mt-1">{String((uploadPhoto.error as Error)?.message ?? 'Upload failed')}</p>
          )}
          <p className="label mt-1">
            JPEG or PNG. The credit is optional; an uncredited photograph is flagged until one is added.
            {d.status === 'published' && ' The live piece is updated too.'}
          </p>
        </div>}
        <div className="flex items-center justify-between mb-1">
          <span className="label">Body</span>
          {!locked && <button type="button" className="label underline" onClick={() => setEditHtml((x) => !x)}>{editHtml ? 'Preview' : 'Edit HTML'}</button>}
        </div>
        {editHtml
          ? <textarea rows={22} value={v('body_html') ?? ''} onChange={(e) => setEdit({ ...edit, body_html: e.target.value })}
                      className="w-full bg-ink-900 border border-ink-600 text-paper text-[13px] p-2 font-mono" />
          : <iframe title="Draft body" sandbox="" className="w-full h-[560px] bg-white border border-ink-500"
                    srcDoc={`<!doctype html><meta charset="utf-8"><style>body{font:16px/1.6 Georgia,serif;color:#0d2340;max-width:720px;margin:24px auto;padding:0 16px}h2{font-size:22px;margin:1.4em 0 .4em}a{color:#8a5f14}</style><h1 style="font-size:30px;line-height:1.15">${escapeHtml(v('title') ?? '')}</h1>${v('body_html') ?? ''}`} />}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {!locked && <Button variant="outline" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>Save edits</Button>}
          {!locked && d.status !== 'rejected' && d.status !== 'failed' && (
            <Button disabled={dirty || approve.isPending} onClick={() => { if (window.confirm(`Publish this piece on ${publishTarget(dest, section.label)} now?`)) approve.mutate() }}>
              {approve.isPending ? 'Publishing…' : 'Approve & publish'}
            </Button>
          )}
          {(d.status === 'failed' || d.status === 'rejected') && (
            // A scheduled run that failed before choosing a subject stores
            // the label '(auto)': retrying that means "the best open subject".
            <Button disabled={running} onClick={() => onRedraft(retryTopic(d.topic), d.brief)}>
              {running ? 'A draft is being written…' : retryTopic(d.topic) ? 'Draft this subject again' : 'Try again'}
            </Button>
          )}
          {canRevise(d.status) && (
            <>
              <Button variant="outline" disabled={running || revising || dirty} title={reviseBlockedHint(dirty) ?? undefined}
                      onClick={() => setRevising(true)}>Revise</Button>
              {dirty && !revising && <span className="label">{reviseBlockedHint(dirty)}</span>}
            </>
          )}
          {(d.status === 'draft' || d.status === 'needs_attention') && (
            <Button onClick={() => addToQueue.mutate()} disabled={addToQueue.isPending}>
              {addToQueue.isPending ? 'Queueing…' : 'Add to queue'}
            </Button>
          )}
          {d.status === 'unpublished' && (
            <Button disabled={approve.isPending} onClick={() => { if (window.confirm(`Publish this piece on ${publishTarget(dest, section.label)} again?`)) approve.mutate() }}>
              {approve.isPending ? 'Publishing…' : 'Publish again'}
            </Button>
          )}
          {d.status === 'published' && (
            <span className="flex items-center gap-2 ml-auto">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional)" className="bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5 w-56" />
              <Button variant="danger" disabled={unpublish.isPending}
                      onClick={() => { if (window.confirm(`Unpublish this piece? It is removed from ${unpublishTarget(d, section.label)} now: each WordPress post becomes a draft.`)) unpublish.mutate() }}>
                {unpublish.isPending ? 'Unpublishing…' : 'Unpublish'}
              </Button>
            </span>
          )}
          {d.status !== 'published' && d.status !== 'rejected' && !superseded && (
            <span className="flex items-center gap-2 ml-auto">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional)" className="bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5 w-56" />
              <Button variant="danger" disabled={reject.isPending} onClick={() => reject.mutate()}>Reject</Button>
            </span>
          )}
        </div>
        {revising && canRevise(d.status) && (
          <ReviseForm draftId={d.id} currentFormat={d.brief?.format} bodyHtml={d.body_html} blockedHint={reviseBlockedHint(dirty)} onClose={() => setRevising(false)}
                      onStarted={() => { setRevising(false); qc.invalidateQueries({ queryKey: ['section-draft-status', PUB] }); onChange() }} />
        )}
        {dirty && <p className="label mt-2">Save your edits before approving.</p>}
        {d.status === 'unpublished' && <p className="label mt-2">Unpublished: the WordPress post is a draft and the piece is off {section.label}. Publish again restores the same post; Reject frees the subject for a new draft.</p>}
        {(approve.error || save.error || reject.error || unpublish.error || addToQueue.error) && <p className="text-red-300 text-[13px] mt-2">{String(approve.error ?? save.error ?? reject.error ?? unpublish.error ?? addToQueue.error)}</p>}
      </Panel>
      {!superseded && <SocialBlock draftId={d.id} social={d.social} orphaned={d.orphaned_posts} postingOn={d.social_posting_on} onChange={refresh} />}
      <Panel label="Sources">
        <ul className="space-y-2 text-[13px]">
          {d.sources.map((s) => (
            <li key={s.id}>
              <span className="label mr-2">{s.id}</span>
              <span className="text-[10px] uppercase tracking-wider px-1 py-0.5 rounded bg-ink-700 text-ink-200 mr-2">{s.kind}</span>
              {s.reference_only && <span className="label normal-case tracking-normal mr-2">reference only</span>}
              {sourceHref(publicBases[d.publication_slug], s) ? <a className="text-paper underline" href={sourceHref(publicBases[d.publication_slug], s)} target="_blank" rel="noreferrer">{s.title}</a> : <span className="text-paper">{s.title}</span>}
              {s.issue_label && <span className="label ml-2">{s.issue_label}</span>}
              <span className="label ml-2">{s.passages.length} passages</span>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel label="Fact check">
        {notFound.length > 0 && (
          <p className="text-amber-200 text-[13px] mb-2">
            {notFound.length} unsupported {notFound.length === 1 ? 'claim' : 'claims'} could not be located and {notFound.length === 1 ? 'is' : 'are'} still in the text. Edit {notFound.length === 1 ? 'it' : 'them'} out before approving.
          </p>
        )}
        <ul className="space-y-1 text-[13px]">
          {(d.verification.claims ?? []).map((c, i) => (
            <li key={i} className={c.supported ? 'text-ink-200' : 'text-amber-200'}>
              <span className="mr-2">{c.supported ? '✓' : '✗'}</span>{c.text}
              {c.supported
                ? <span className="label ml-2">{c.source_id}</span>
                : <span className={`label ml-2 ${notFound.includes(c.text) ? 'text-red-300' : ''}`}>
                    {(c.note ?? 'not in sources') + fate(c.text)}
                  </span>}
            </li>
          ))}
        </ul>
        {(d.verification.claims ?? []).length === 0 && <p className="label">No claims recorded.</p>}
      </Panel>
    </div>
  )
}

/** "View on {host}" from the piece's own public URL — never a hardcoded
 *  title. Falls back to "View live" when the URL doesn't parse. */
function viewLinkLabel(url: string): string {
  try {
    return `View on ${new URL(url).host}`
  } catch {
    return 'View live'
  }
}
